import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./auth";

/**
 * Global tutorial control. Auto-opens once per device for brand-new users
 * (onboarded but zero EXP) when they land on the dashboard, so a returning
 * lifter signing in on a new phone doesn't get a tutorial in the face.
 * Exposes `open()` and `close()` so the Menu dropdown can relaunch on demand.
 *
 * State is persisted to localStorage (not the backend) — re-installing the
 * PWA or signing in on a new device shows the tutorial again ONLY if the
 * account is brand-new (0 EXP). Returning users explicitly open it from the
 * Menu drawer.
 */
const TutorialCtx = createContext({ openTutorial: () => {}, closeTutorial: () => {}, isOpen: false });

const STORAGE_KEY = "hg_tutorial_v2_seen";
// Routes where it's acceptable to auto-pop the tutorial. Don't pop mid-checkout,
// during onboarding, or while the user is logging a workout session.
const AUTO_OPEN_ROUTES = new Set(["/dashboard", "/"]);

export function TutorialProvider({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [autoOpenedThisSession, setAutoOpenedThisSession] = useState(false);

    // Auto-open once per device for brand-new accounts. Gates:
    //   1. User is loaded + onboarded
    //   2. total_exp === 0 (truly fresh — has never logged a working set)
    //   3. Not already seen on this device
    //   4. Current route is dashboard-y (don't yank focus mid-flow)
    //   5. Haven't already auto-opened this session
    useEffect(() => {
        if (autoOpenedThisSession) return;
        if (!user || !user.onboarded) return;
        if ((user.total_exp ?? 0) > 0) return;
        if (!AUTO_OPEN_ROUTES.has(location.pathname)) return;
        try {
            if (localStorage.getItem(STORAGE_KEY) === "true") return;
        } catch (_) { /* localStorage might be blocked in private mode */ }
        // Delay so the dashboard chrome paints first and spotlight targets exist.
        const t = setTimeout(() => {
            setIsOpen(true);
            setAutoOpenedThisSession(true);
        }, 1500);
        return () => clearTimeout(t);
    }, [user, location.pathname, autoOpenedThisSession]);

    const openTutorial = () => setIsOpen(true);
    const closeTutorial = (markSeen = true) => {
        setIsOpen(false);
        if (markSeen) {
            try { localStorage.setItem(STORAGE_KEY, "true"); } catch (_) {}
        }
    };

    return (
        <TutorialCtx.Provider value={{ openTutorial, closeTutorial, isOpen }}>
            {children}
        </TutorialCtx.Provider>
    );
}

export function useTutorial() {
    return useContext(TutorialCtx);
}
